import { createSlice } from '@reduxjs/toolkit';

import { EmploymentViewModel, EmploymentViewModelNew } from '@fora-monorepo/employer-my-pages-common/types';
import { ThunkRequestStatus } from '@fora-monorepo/webapp-types';
import { trackException } from '@fora-monorepo/webapp-analytics';

import {
  addEmployment,
  addEmploymentOld,
  getCurrentEmployments,
  getCurrentEmploymentsNew,
  getEmployerHasEmployments,
  getEmploymentReportStatus,
  updateEmployment,
  updateEmploymentOld,
} from './employmentThunks';
import { EmploymentState } from './types/index';

export const initialEmploymentState: EmploymentState = {
  employments: [],
  employmentsNew: [],
  employerHasEmployments: true,
  employmentReportStatus: null,
  loadingEmployerHasEmployments: ThunkRequestStatus.Pending,
  loadingCurrentEmployments: ThunkRequestStatus.Pending,
  loadingEmploymentReportStatus: ThunkRequestStatus.Pending,
};

const employmentSlice = createSlice({
  name: 'employments',
  initialState: initialEmploymentState,
  reducers: {},
  extraReducers: builder => {
    // Get employments
    builder.addCase(getCurrentEmployments.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingCurrentEmployments = ThunkRequestStatus.Fulfilled;
        state.employments = action.payload;
      }
    });
    builder.addCase(getCurrentEmployments.pending, state => {
      state.loadingCurrentEmployments = ThunkRequestStatus.Pending;
    });
    builder.addCase(getCurrentEmployments.rejected, state => {
      state.loadingCurrentEmployments = ThunkRequestStatus.Rejected;
    });
    // Get employments new (with employment periods)
    builder.addCase(getCurrentEmploymentsNew.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingCurrentEmployments = ThunkRequestStatus.Fulfilled;
        state.employmentsNew = action.payload;
      }
    });
    builder.addCase(getCurrentEmploymentsNew.pending, state => {
      state.loadingCurrentEmployments = ThunkRequestStatus.Pending;
    });
    builder.addCase(getCurrentEmploymentsNew.rejected, state => {
      state.loadingCurrentEmployments = ThunkRequestStatus.Rejected;
    });
    // Get organization has employments
    builder.addCase(getEmployerHasEmployments.fulfilled, (state, action) => {
      if (action.payload) {
        state.loadingEmployerHasEmployments = ThunkRequestStatus.Fulfilled;
        state.employerHasEmployments = action.payload.employerHasEmployments;
      }
    });
    builder.addCase(getEmployerHasEmployments.pending, state => {
      state.loadingEmployerHasEmployments = ThunkRequestStatus.Pending;
    });
    builder.addCase(getEmployerHasEmployments.rejected, state => {
      state.loadingEmployerHasEmployments = ThunkRequestStatus.Rejected;
    });
    // Add new employment (old)
    builder.addCase(addEmploymentOld.fulfilled, (state, action) => {
      const addEmployment: EmploymentViewModel = action.payload;
      if (action.payload) {
        state.employments.push(addEmployment);
      }
    });
    // Add new employment
    builder.addCase(addEmployment.fulfilled, (state, action) => {
      const addEmployment: EmploymentViewModelNew = action.payload;
      if (action.payload) {
        state.employmentsNew.push(addEmployment);
      }
    });

    // Update employment
    builder.addCase(updateEmployment.fulfilled, (state, action) => {
      const updatedEmployment: EmploymentViewModelNew = action.payload;
      if (updatedEmployment) {
        const index = state.employmentsNew.findIndex(
          employment => updatedEmployment.policyNumber === employment.policyNumber
        );
        if (index !== -1) {
          state.employmentsNew[index] = updatedEmployment;
        } else {
          trackException(
            new Error(
              `Updated employment with policyNumber: ${updatedEmployment.policyNumber} does not exist in state.`
            )
          );
        }
      }
    });

    // Update employment (old)
    builder.addCase(updateEmploymentOld.fulfilled, (state, action) => {
      const updatedEmployment: EmploymentViewModel = action.payload;
      if (updatedEmployment) {
        const index = state.employments.findIndex(
          employment => updatedEmployment.policyNumber === employment.policyNumber
        );
        if (index !== -1) {
          state.employments[index] = updatedEmployment;
        } else {
          trackException(
            new Error(
              `Updated employment with policyNumber: ${updatedEmployment.policyNumber} does not exist in state.`
            )
          );
        }
      }
    });
    // Get status for reports with employment data
    builder.addCase(getEmploymentReportStatus.pending, state => {
      state.loadingEmploymentReportStatus = ThunkRequestStatus.Pending;
    });
    builder.addCase(getEmploymentReportStatus.fulfilled, (state, action) => {
      state.employmentReportStatus = action.payload;
      state.loadingEmploymentReportStatus = ThunkRequestStatus.Fulfilled;
    });
    builder.addCase(getEmploymentReportStatus.rejected, state => {
      state.loadingEmploymentReportStatus = ThunkRequestStatus.Rejected;
    });
  },
});

export const employmentReducer = employmentSlice.reducer;
